import {
  getLocationTier,
  getUserStateAndLGA,
  LOCATION_TIER_ORDER,
  type LocationTier,
  type UserLocationForHierarchy,
} from "@/lib/locationHierarchy";

export type HomepageDiscoveryGranularity = "neighborhood" | "lga" | "state";

export const HOMEPAGE_DISCOVERY_GRANULARITY_OPTIONS: readonly HomepageDiscoveryGranularity[] =
  ["neighborhood", "lga", "state"] as const;

export const DEFAULT_HOMEPAGE_DISCOVERY_GRANULARITY: HomepageDiscoveryGranularity =
  "lga";

export function parseHomepageDiscoveryGranularity(
  raw: unknown,
): HomepageDiscoveryGranularity {
  if (raw === "neighborhood" || raw === "lga" || raw === "state") {
    return raw;
  }
  return DEFAULT_HOMEPAGE_DISCOVERY_GRANULARITY;
}

export function granularityToMaxTier(
  granularity: HomepageDiscoveryGranularity,
): LocationTier {
  switch (granularity) {
    case "neighborhood":
      return "area";
    case "lga":
      return "lga";
    case "state":
      return "state";
  }
}

export function widenDiscoveryGranularityOneStep(
  granularity: HomepageDiscoveryGranularity,
): HomepageDiscoveryGranularity {
  if (granularity === "neighborhood") return "lga";
  if (granularity === "lga") return "state";
  return "state";
}

export function isTierWithinDiscoveryReach(
  tier: LocationTier,
  maxTier: LocationTier,
  includeOtherLocationTier: boolean,
): boolean {
  if (tier === "other") {
    // "other" (out of the buyer's state) is a spillover BEYOND the in-country
    // reach, not a shortcut around it. It only becomes reachable once discovery
    // has already widened to the whole state — otherwise we'd surface far
    // out-of-state items while still hiding nearer same-state (other-LGA) ones.
    return includeOtherLocationTier && maxTier === "state";
  }
  return LOCATION_TIER_ORDER[tier] <= LOCATION_TIER_ORDER[maxTier];
}

export function getItemLocationTier(
  itemLocation: string | undefined,
  userLocation: UserLocationForHierarchy,
): LocationTier {
  const { state: userState, lga: userLGA } = getUserStateAndLGA(userLocation);
  const areaName = userLocation.areaName ?? "";
  return getLocationTier(itemLocation ?? "", areaName, userState, userLGA);
}

export function filterItemsByDiscoveryGranularity<
  T extends { location?: string },
>(
  items: T[],
  userLocation: UserLocationForHierarchy | null,
  granularity: HomepageDiscoveryGranularity,
  includeOtherLocationTier: boolean,
): T[] {
  if (!userLocation?.areaName?.trim()) {
    return items;
  }

  const maxTier = granularityToMaxTier(granularity);
  return items.filter((item) => {
    const tier = getItemLocationTier(item.location, userLocation);
    return isTierWithinDiscoveryReach(tier, maxTier, includeOtherLocationTier);
  });
}

export function resolveEffectiveDiscoveryGranularity<
  T extends { location?: string },
>(
  items: T[],
  userLocation: UserLocationForHierarchy | null,
  options: {
    configured: HomepageDiscoveryGranularity;
    autoWiden: boolean;
    minCount: number;
    includeOtherLocationTier: boolean;
  },
): HomepageDiscoveryGranularity {
  if (!userLocation?.areaName?.trim() || options.minCount <= 0) {
    return options.configured;
  }

  let effective = options.configured;
  while (true) {
    const count = filterItemsByDiscoveryGranularity(
      items,
      userLocation,
      effective,
      options.includeOtherLocationTier,
    ).length;

    if (
      count >= options.minCount ||
      !options.autoWiden ||
      effective === "state"
    ) {
      return effective;
    }

    effective = widenDiscoveryGranularityOneStep(effective);
  }
}

export function getHomepageApprovedPoolLimit(
  granularity: HomepageDiscoveryGranularity,
): number {
  return granularity === "state" ? 90 : 60;
}

/**
 * Minimum items the main homepage discovery shelf aims to show before it stops
 * auto-widening its location reach. Keeps the horizontal row from looking empty
 * (or dropping straight to out-of-state spillover) when the neighborhood/LGA is
 * thin, by widening to the whole state first.
 */
export const HOMEPAGE_DISCOVERY_SHELF_MIN_ITEMS = 8;
